import {
  CalendarDate,
  DateValue,
  getLocalTimeZone,
  today,
} from '@internationalized/date';
import { TFunction } from 'i18next';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import { supportedLocales } from '@provider-directory/app/i18n/supported-locales';
import { DAYS_RANGE } from '@provider-directory/constants';
import { formatDateToFullDateString } from '@provider-directory/functions/date';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';

export const TimeZoneContext = createContext(getLocalTimeZone());

export const useTimeZone = (): string => useContext(TimeZoneContext);
export interface FormValuesDateRangePicker {
  dateRange: {
    start: DateValue;
    end: DateValue;
  };
}
export const useFetchableDateRange = (): [CalendarDate, CalendarDate] => {
  const maxDays = useAvailabilityWindow();

  return useMemo(() => {
    const todaysDate = today('EST');
    return [todaysDate, todaysDate.add({ days: maxDays })];
  }, [maxDays]);
};
export interface RangeValue<T> {
  /** The start value of the range. */
  start: T;
  /** The end value of the range. */
  end: T;
}
export interface ISearchCalendarProps {
  value: {
    startDate: DateValue | null;
    endDate: DateValue | null;
  };
  timeStampHandler: (date: DateValue) => string;
  isOpen: boolean;
  toggleOpen: () => void;
  setIsOpen: (isOpen: boolean) => void;
  onChange: ({
    startDate,
    endDate,
    shouldSetPreviousRange,
  }: {
    startDate: DateValue;
    endDate?: DateValue;
    shouldSetPreviousRange?: boolean;
  }) => void;
  isDesktop: boolean;
  previousRange: {
    startDate: DateValue;
    endDate: DateValue;
  } | null;
  setPreviousRange: Dispatch<
    SetStateAction<{
      startDate: DateValue;
      endDate: DateValue;
    } | null>
  >;
  timeZone: string;
  t: TFunction;
  locale: string;
  lastFetchableDate: CalendarDate;
  todaysDate: DateValue;
}
export interface IDefaultCalendarProps {
  defaultValue?: RangeValue<DateValue>;
}
export const useCalendarHook = ({
  defaultValue,
}: IDefaultCalendarProps = {}): ISearchCalendarProps => {
  const timeZone = useTimeZone();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [isOpen, toggleOpen, setIsOpen] = useToggle(false);

  const [todaysDate, lastFetchableDate] = useFetchableDateRange();
  const [value, setSelectedDates] = useState<{
    startDate: DateValue | null;
    endDate: DateValue | null;
  }>(
    defaultValue
      ? { startDate: defaultValue.start, endDate: defaultValue.end }
      : {
          startDate: today('EST'),
          endDate: today('EST').add({ days: DAYS_RANGE }),
        },
  );

  const isDesktop = useIsDesktop();
  const [previousRange, setPreviousRange] = useState<{
    startDate: DateValue;
    endDate: DateValue;
  } | null>({
    startDate: todaysDate,
    endDate: todaysDate.add({ days: DAYS_RANGE }),
  });
  const onChange = ({
    startDate,
    endDate,
    shouldSetPreviousRange = false,
  }: {
    startDate: DateValue;
    endDate?: DateValue;
    shouldSetPreviousRange?: boolean;
  }): void => {
    setSelectedDates({
      startDate,
      endDate: endDate ?? startDate.add({ days: DAYS_RANGE }),
    });
    if (shouldSetPreviousRange) {
      setPreviousRange({
        startDate,
        endDate: endDate ?? startDate.add({ days: DAYS_RANGE }),
      });
    }
  };

  const timeStampHandler = (dateValue: DateValue) =>
    formatDateToFullDateString({
      dateString: dateValue.toString(),
      locale: supportedLocales.get(locale)?.locale,
      dateFormat: 'EEE, MMMM d, yyyy',
    });

  return {
    onChange,
    value,
    isDesktop,
    previousRange,
    setPreviousRange,
    timeZone,
    t,
    locale,
    todaysDate,
    lastFetchableDate,
    isOpen,
    toggleOpen,
    setIsOpen,
    timeStampHandler,
  };
};
